import { Controller } from "@hotwired/stimulus";
import DmnJS from "dmn-js";

export default class extends Controller {
  static targets = ["container"];

  static values = {
    source: String,
    decisionId: { type: String, default: "" },
  };

  connect() {
    // Clean up any existing viewer before initializing a new one
    this.cleanupViewer();
    this.initializeViewer();
  }

  cleanupViewer() {
    if (this.dmnViewer) {
      try {
        this.dmnViewer.destroy();
      } catch (e) {
        console.error("Error while cleaning up DMN viewer:", e);
      } finally {
        this.dmnViewer = null;
        // Clear the container content to prevent duplicates
        if (this.hasContainerTarget) {
          this.containerTarget.innerHTML = "";
        }
      }
    }
  }

  async initializeViewer() {
    // Initialize DMN viewer in the container area
    this.dmnViewer = new DmnJS({
      container: this.containerTarget,
      drd: {
        additionalModules: [],
      },
      decisionTable: {
        additionalModules: [],
        keyboard: {
          bindTo: document,
        },
      },
      literalExpression: {
        additionalModules: [],
      },
    });

    // Get the DMN diagram source and display it
    const diagramSource = this.sourceValue;

    if (diagramSource) {
      try {
        // Use async/await pattern with Promise
        const { warnings } = await this.importDiagram(diagramSource);

        if (warnings && warnings.length) {
          console.warn("Warnings while rendering DMN diagram:", warnings);
        }

        // Get all available views
        const views = this.dmnViewer.getViews();
        if (views && views.length > 0) {
          const decisionId = this.decisionIdValue;

          // If we have a specific decision ID, try to find and open that view
          if (decisionId) {
            const targetView = views.find(
              (view) => view.element && view.element.id === decisionId
            );

            if (targetView) {
              // Open the specific decision
              this.dmnViewer.open(targetView);
              console.log(`Opened decision with ID: ${decisionId}`);
            } else {
              // If decision ID not found, open the first view and log a warning
              console.warn(
                `Decision with ID '${decisionId}' not found. Opening default view instead.`
              );
              this.dmnViewer.open(views[0]);
            }
          } else {
            // No decision ID provided, open the first view (usually the DRD)
            this.dmnViewer.open(views[0]);
          }

          // fetch currently active view
          const activeView = this.dmnViewer.getActiveView();

          // apply initial logic in DRD view
          if (activeView.type === "drd") {
            const activeEditor = this.dmnViewer.getActiveViewer();

            // access active editor components
            const canvas = activeEditor.get("canvas");

            // zoom to fit full viewport
            canvas.zoom("fit-viewport");
          }
        }
      } catch (err) {
        console.error("Error rendering DMN diagram:", err);
        this.showErrorMessage("Failed to render DMN diagram");
      }
    } else {
      this.showErrorMessage("No DMN source provided");
    }
  }

  async importDiagram(xml) {
    // importXML now returns a Promise directly.
    const { warnings } = await this.dmnViewer.importXML(xml);
    return { warnings };
  }

  showErrorMessage(message) {
    // Display an error message in the container
    if (this.hasContainerTarget) {
      this.containerTarget.innerHTML = `
        <div class="p-4 bg-red-50 border border-red-200 rounded text-red-600">
          <p>${message}</p>
        </div>
      `;
    }
  }

  disconnect() {
    // Clean up the viewer when the controller is disconnected
    this.cleanupViewer();
  }
}
